import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaChalkboardTeacher } from 'react-icons/fa';
import { Modal } from '../components/common/Modal';
import { ClassForm } from '../components/discipleship/ClassForm';
import { ClassList } from '../components/discipleship/ClassList';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { discipleshipService } from '../services/discipleship.service';
import { studentService } from '../services/student.service';
import type { DiscipleshipClass } from '../types/discipleship';
import { useNotification } from '../contexts/NotificationContext'; // Import useNotification

interface ClassStudentStats {
  [classId: string]: {
    total: number;
    completed: number;
  };
}

type ClassComputedStatus = '진행중' | '완료됨' | '시작전' | '날짜미정';

const ITEMS_PER_PAGE = 5;

// 그룹화 설정
const GROUP_CONFIG = {
  새가족반: ['신입성도반', '새가족반', '기초반', '입문반', '초급반'],
  사역반: ['사역자양반', '지도자양반', '목사양반', '사역양반', '사역반'],
  성장반: ['성도양반', '중급반', '고급반', '성장반'],
  제자반: ['제자반', '심화반', '전도양반', '선교양반'],
  기타: ['기타', '특별반', '기타양반', '특강반', '세미나']
};

export const DiscipleshipPage: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [allClasses, setAllClasses] = useState<DiscipleshipClass[]>([]);
  const [classStudentStats, setClassStudentStats] = useState<ClassStudentStats>({});
  const [loading, setLoading] = useState(true);
  const [loadingStats, setLoadingStats] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);
  const [editingClass, setEditingClass] = useState<DiscipleshipClass | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const [currentPage, setCurrentPage] = useState(1);
  
  const [selectedTeacher, setSelectedTeacher] = useState<string>("");
  const [selectedStatus, setSelectedStatus] = useState<ClassComputedStatus | "">("");

  const navigate = useNavigate();
  const { addNotification } = useNotification(); // Use notification hook
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const calculateClassStatus = useCallback((cls: DiscipleshipClass): ClassComputedStatus => {
    if (!cls.startDate) return '날짜미정';
    try {
      const startDate = new Date(cls.startDate);
      if (isNaN(startDate.getTime())) return '날짜미정'; 

      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      if (startDate > today) {
        return '시작전';
      }
      
      const durationWeeks = cls.durationInWeeks || 0; 
      const endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + durationWeeks * 7);
      
      if (endDate <= today) { 
        return '완료됨';
      }
      return '진행중';
    } catch (e) {
      console.error("Error calculating class status: ", e);
      return '날짜미정';
    }
  }, []);

  const isClassCompleted = useCallback((cls: DiscipleshipClass): boolean => {
    return calculateClassStatus(cls) === '완료됨';
  }, [calculateClassStatus]);

  const fetchStatsForClass = useCallback(async (classId: string) => {
    setLoadingStats(prev => ({ ...prev, [classId]: true }));
    try {
      const students = await studentService.getStudentsForClass(classId);
      const total = students.length;
      const completed = students.filter(s => s.completionStatus === '수료').length;
      setClassStudentStats(prev => ({
        ...prev,
        [classId]: { total, completed }
      }));
    } catch (statError) {
      console.error(`Failed to fetch student stats for class ${classId}:`, statError);
      setClassStudentStats(prev => ({
        ...prev,
        [classId]: { total: 0, completed: 0 }
      }));
    } finally {
      setLoadingStats(prev => ({ ...prev, [classId]: false }));
    }
  }, []);

  const fetchClasses = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setClassStudentStats({});
      const data = await discipleshipService.getAllClasses(); 
      setAllClasses(data); 
      data.forEach(cls => {
        fetchStatsForClass(cls.id);
      });
    } catch (err) {
      console.error(err);
      setError("양육 과정 목록을 불러오는 중 오류가 발생했습니다. Firestore 색인 설정을 확인해주세요.");
      addNotification("양육 과정 목록 로딩 중 오류 발생", "error");
    } finally {
      setLoading(false);
    }
  }, [fetchStatsForClass, addNotification]);

  useEffect(() => {
    fetchClasses();
  }, [fetchClasses]);

  useEffect(() => {
    if (!searchTerm) {
      setSuggestions([]);
      setShowSuggestions(false);
      setActiveSuggestionIndex(-1);
    } else {
      const lowerSearchTerm = searchTerm.toLowerCase();
      const uniqueProgramNames = Array.from(
        new Set(
          allClasses
            .map(cls => cls.programName === 'other' && cls.programNameOther ? cls.programNameOther : cls.programName)
            .filter(name => name?.toLowerCase().includes(lowerSearchTerm))
            .filter((name): name is string => typeof name === 'string') 
        )
      ).slice(0, 7);
      setSuggestions(uniqueProgramNames);
      setShowSuggestions(uniqueProgramNames.length > 0);
      setActiveSuggestionIndex(uniqueProgramNames.length > 0 ? 0 : -1);
    }
    setCurrentPage(1); 
  }, [searchTerm, allClasses]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedTeacher, selectedStatus]);

  const handleFormSuccess = () => {
    setIsModalOpen(false);
    setEditingClass(null); 
    fetchClasses(); 
  };
  
  const handleAddNew = () => {
    setEditingClass(null);
    setIsModalOpen(true);
  };

  const handleEdit = (cls: DiscipleshipClass) => {
    setEditingClass(cls);
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm("정말로 이 과정을 삭제하시겠습니까? 이 과정에 등록된 모든 수강생 정보도 함께 삭제됩니다.")) {
      try {
        await discipleshipService.deleteClass(id);
        addNotification("과정이 성공적으로 삭제되었습니다.", "success");
        fetchClasses(); 
      } catch (err) {
        console.error("삭제 중 오류:", err);
        addNotification("삭제 중 오류가 발생했습니다.", "error");
      }
    }
  };

  const handleManageStudents = (id: string) => {
    navigate(`/discipleship/${id}/students`);
  };

  const handleSuggestionClick = (suggestion: string) => {
    setSearchTerm(suggestion);
    setShowSuggestions(false);
    setActiveSuggestionIndex(-1);
    setCurrentPage(1); 
    searchInputRef.current?.focus();
  };
  
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (showSuggestions && suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveSuggestionIndex(prev => (prev + 1) % suggestions.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveSuggestionIndex(prev => (prev - 1 + suggestions.length) % suggestions.length);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (activeSuggestionIndex >= 0 && activeSuggestionIndex < suggestions.length) {
          handleSuggestionClick(suggestions[activeSuggestionIndex]);
        }
      } else if (e.key === 'Escape') {
        setShowSuggestions(false);
        setActiveSuggestionIndex(-1);
      }
    } else if (e.key === 'ArrowDown' && suggestions.length > 0) {
        setShowSuggestions(true);
        setActiveSuggestionIndex(0);
    }
  };
  
  const uniqueTeachers = Array.from(new Set(allClasses.map(cls => cls.teacher))).sort();
  const classStatuses: ClassComputedStatus[] = ['시작전', '진행중', '완료됨', '날짜미정'];

  const getFilteredAndSortedClasses = () => {
    let filtered = allClasses;
    if (searchTerm) {
      filtered = filtered.filter(cls => {
        const displayName = cls.programName === 'other' && cls.programNameOther ? cls.programNameOther : cls.programName;
        return displayName?.toLowerCase().includes(searchTerm.toLowerCase());
      });
    }
    if (selectedTeacher) {
      filtered = filtered.filter(cls => cls.teacher === selectedTeacher);
    }
    if (selectedStatus) {
      filtered = filtered.filter(cls => calculateClassStatus(cls) === selectedStatus);
    }
    return [...filtered].sort((a, b) => {
        const aName = (a.programName === 'other' && a.programNameOther ? a.programNameOther : a.programName) || "";
        const bName = (b.programName === 'other' && b.programNameOther ? b.programNameOther : b.programName) || "";
        if (aName.localeCompare(bName) !== 0) return aName.localeCompare(bName);
        if (b.createdAt.seconds !== a.createdAt.seconds) return b.createdAt.seconds - a.createdAt.seconds;
        return b.createdAt.nanoseconds - a.createdAt.nanoseconds;
    });
  };

  // 클래스를 그룹으로 분류
  const getGroupedClasses = () => {
    const grouped: Record<string, DiscipleshipClass[]> = {};
    
    // 그룹 초기화
    Object.keys(GROUP_CONFIG).forEach(group => {
      grouped[group] = [];
    });

    const filteredClasses = getFilteredAndSortedClasses();

    filteredClasses.forEach(cls => {
      const displayName = cls.programName === 'other' && cls.programNameOther ? cls.programNameOther : cls.programName;
      let assignedGroup = '기타'; // 기본값

      // 그룹에 할당
      for (const [groupName, keywords] of Object.entries(GROUP_CONFIG)) {
        if (groupName === '기타') continue; // 기타는 마지막에 처리
        
        if (keywords.some(keyword => displayName?.includes(keyword))) {
          assignedGroup = groupName;
          break;
        }
      }

      grouped[assignedGroup].push(cls);
    });

    return grouped;
  };

  // 그룹별 페이지네이션 상태
  const [groupPageStates, setGroupPageStates] = useState<Record<string, number>>({});

  const handleExportCSV = () => {
    const classesToExport = getFilteredAndSortedClasses();
    if (classesToExport.length === 0) {
      addNotification("내보낼 데이터가 없습니다.", "info");
      return;
    }

    const headers = ["과정 이름", "상태", "기수", "개설일", "담당자", "기간 (주)", "등록인원", "수료인원"];
    const csvRows = [headers.join(',')];

    classesToExport.forEach(cls => {
      const displayName = cls.programName === 'other' && cls.programNameOther ? cls.programNameOther : cls.programName;
      const status = calculateClassStatus(cls);
      const creationDate = cls.createdAt ? new Date(cls.createdAt.seconds * 1000).toLocaleDateString('ko-KR') : '-';
      const stats = classStudentStats[cls.id];
      const registeredCount = stats ? stats.total : (loadingStats[cls.id] ? '로딩중' : 0);
      const completedCount = isClassCompleted(cls) && stats ? stats.completed : (isClassCompleted(cls) && loadingStats[cls.id] ? '로딩중' : '-');
      
      const row = [
        `"${displayName}"`,
        status,
        `"${cls.session}"`,
        creationDate,
        `"${cls.teacher}"`,
        cls.durationInWeeks,
        registeredCount,
        completedCount
      ].join(',');
      csvRows.push(row);
    });

    const csvString = csvRows.join('\n');
    const blob = new Blob([`\uFEFF${csvString}`], { type: 'text/csv;charset=utf-8;' }); // BOM for Excel
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const dateStr = new Date().toISOString().slice(0,10).replace(/-/g,"");
    link.setAttribute('href', url);
    link.setAttribute('download', `양육과정_목록_${dateStr}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    addNotification("CSV 파일이 다운로드되었습니다.", "success");
  };

  const renderContent = () => {
    if (loading && allClasses.length === 0) return <div className="flex justify-center items-center py-10"><LoadingSpinner /></div>;
    if (error) return <p className="text-center text-red-500 bg-red-100 p-4 rounded-md shadow">{error}</p>;
    
    const groupedClasses = getGroupedClasses();
    const hasClasses = Object.values(groupedClasses).some(classes => classes.length > 0);

    if (!hasClasses) {
      if (searchTerm || selectedTeacher || selectedStatus) {
        return <div className="p-6 bg-gray-50 dark:bg-gray-700 rounded-lg shadow-inner text-center text-gray-500 dark:text-gray-400">선택된 조건에 맞는 과정이 없습니다.</div>;
      }
      return <div className="p-6 bg-gray-50 dark:bg-gray-700 rounded-lg shadow-inner text-center text-gray-500 dark:text-gray-400">개설된 양육 과정이 없습니다. '새 과정 개설' 버튼을 통해 새로운 과정을 만들어보세요.</div>;
    }
    
    return (
      <div className="space-y-8">
        {Object.entries(groupedClasses).map(([groupName, classes]) => {
          if (classes.length === 0) return null;
          
          const groupPage = groupPageStates[groupName] || 1;
          const totalItems = classes.length;
          const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
          const startIndex = (groupPage - 1) * ITEMS_PER_PAGE;
          const endIndex = startIndex + ITEMS_PER_PAGE;
          const paginatedClasses = classes.slice(startIndex, endIndex);

          // 그룹별 색상 설정
          const groupColors = {
            '새가족반': 'from-green-500 to-green-600',
            '사역반': 'from-blue-500 to-blue-600',
            '성장반': 'from-purple-500 to-purple-600',
            '제자반': 'from-orange-500 to-orange-600',
            '기타': 'from-gray-500 to-gray-600'
          };

          return (
            <div key={groupName} className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
              {/* 그룹 헤더 */}
              <div className={`bg-gradient-to-r ${groupColors[groupName as keyof typeof groupColors] || groupColors['기타']} text-white p-6`}>
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold">{groupName}</h2>
                    <p className="text-blue-100 mt-1">총 {classes.length}개 과정</p>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold">{classes.length}</div>
                    <div className="text-sm text-blue-100">개 과정</div>
                  </div>
                </div>
              </div>

              {/* 그룹별 과정 목록 */}
              <div className="p-6 space-y-6">
                {paginatedClasses.map((classInstance, index) => {
                  const currentProgramDisplayName = classInstance.programName === 'other' && classInstance.programNameOther
                    ? classInstance.programNameOther : classInstance.programName;
                  const stats = classStudentStats[classInstance.id];
                  const completed = isClassCompleted(classInstance);
                  const isLoadingThisStat = loadingStats[classInstance.id];
                  const computedStatus = calculateClassStatus(classInstance);

                  return (
                    <div key={classInstance.id} className={index === 0 ? '' : 'mt-6'}>
                      <div className="rounded-lg shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
                        <ClassList
                          classes={[classInstance]}
                          onEdit={handleEdit}
                          onDelete={handleDelete}
                          onManageStudents={handleManageStudents}
                          studentCount={isLoadingThisStat && stats === undefined ? undefined : stats?.total}
                          completedStudentCount={isLoadingThisStat && stats === undefined ? undefined : stats?.completed}
                          isClassCompleted={completed}
                          classComputedStatus={computedStatus}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* 그룹별 페이지네이션 */}
              {totalPages > 1 && (
                <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 bg-gray-50 dark:bg-gray-700">
                  <div className="flex justify-center items-center space-x-3">
                    <button
                      onClick={() => setGroupPageStates(prev => ({ ...prev, [groupName]: Math.max(prev[groupName] - 1, 1) }))}
                      disabled={groupPage === 1}
                      className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg shadow-sm hover:bg-gray-300 dark:hover:bg-gray-500 disabled:opacity-50 transition-colors"
                      aria-label={`${groupName} 이전 페이지`}
                    >
                      이전
                    </button>
                    <span className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                      페이지 {groupPage} / {totalPages}
                    </span>
                    <button
                      onClick={() => setGroupPageStates(prev => ({ ...prev, [groupName]: Math.min(prev[groupName] + 1, totalPages) }))}
                      disabled={groupPage === totalPages}
                      className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg shadow-sm hover:bg-gray-300 dark:hover:bg-gray-500 disabled:opacity-50 transition-colors"
                      aria-label={`${groupName} 다음 페이지`}
                    >
                      다음
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="p-8">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6 gap-4">
        <div className="flex items-center">
          <FaChalkboardTeacher className="text-3xl text-gray-600 dark:text-gray-200 mr-4" />
          <div>
            <h1 className="text-3xl font-bold text-gray-600 dark:text-gray-200">양육관리</h1>
            <p className="mt-1 text-md text-gray-600 dark:text-gray-400">교회 양육 프로그램을 개설하고 수강생을 관리합니다.</p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
            <button 
              onClick={handleExportCSV} 
              className="px-4 py-2 bg-[#0096c7] hover:bg-[#023e8a] text-white font-medium rounded-lg shadow-xl focus:outline-none focus:ring-2 focus:ring-[#0096c7] focus:ring-opacity-50 transition duration-150 ease-in-out flex items-center justify-center whitespace-nowrap text-sm"
              aria-label="CSV로 내보내기"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 inline mr-1.5 -ml-0.5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
              CSV 내보내기
            </button>
            <button 
              onClick={handleAddNew} 
              className="px-4 py-2 bg-[#90a955] hover:bg-[#31572c] text-white font-medium rounded-lg shadow-xl focus:outline-none focus:ring-2 focus:ring-[#90a955] focus:ring-opacity-50 transition duration-150 ease-in-out flex items-center justify-center whitespace-nowrap"
              aria-label="새 양육 과정 개설"
            >
              + 새 과정 개설
            </button>
        </div>
      </div>

      <div className="mt-8">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg">
          <div ref={searchContainerRef} className="mb-4 relative">
            <input
              ref={searchInputRef}
              type="text"
              placeholder="과정 이름으로 검색..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              onFocus={() => { if (searchTerm && suggestions.length > 0) setShowSuggestions(true); }}
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 transition duration-150 ease-in-out"
              aria-label="양육 과정 검색"
              aria-autocomplete="list"
              aria-controls="suggestions-listbox"
              aria-expanded={showSuggestions && suggestions.length > 0}
              aria-activedescendant={activeSuggestionIndex >= 0 ? `suggestion-item-${activeSuggestionIndex}` : undefined}
            />
            {showSuggestions && suggestions.length > 0 && (
              <ul 
                id="suggestions-listbox"
                className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto"
                role="listbox" aria-label="검색 제안"
              >
                {suggestions.map((suggestion, index) => (
                  <li
                    id={`suggestion-item-${index}`} key={index}
                    onClick={() => handleSuggestionClick(suggestion)}
                    onMouseDown={(e) => e.preventDefault()} 
                    className={`px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer text-sm text-gray-700 dark:text-gray-300 ${index === activeSuggestionIndex ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300' : ''}`}
                    role="option" aria-selected={index === activeSuggestionIndex}
                  >
                    {suggestion}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-6">
            <div className="sm:col-span-2 md:col-span-1">
              <label htmlFor="teacherFilter" className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">담당자 필터</label>
              <select
                id="teacherFilter" value={selectedTeacher}
                onChange={(e) => setSelectedTeacher(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 transition duration-150 ease-in-out text-sm"
              >
                <option value="">모든 담당자</option>
                {uniqueTeachers.map(teacher => (<option key={teacher} value={teacher}>{teacher}</option>))}
              </select>
            </div>
            <div className="sm:col-span-2 md:col-span-1">
              <label htmlFor="statusFilter" className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">상태 필터</label>
              <select
                id="statusFilter" value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value as ClassComputedStatus | "")}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 transition duration-150 ease-in-out text-sm"
              >
                <option value="">모든 상태</option>
                {classStatuses.map(status => (<option key={status} value={status}>{status}</option>))}
              </select>
            </div>
          </div>
        </div>
      </div>
      
      <div className="mt-8">
        {renderContent()}
      </div>
      
      <Modal 
        isOpen={isModalOpen} 
        onClose={() => { setIsModalOpen(false); setEditingClass(null); }} 
        title={editingClass ? "과정 정보 수정" : "새 양육 과정 개설"}
      >
        <ClassForm onSuccess={handleFormSuccess} editingClass={editingClass} />
      </Modal>
    </div>
  );
};
